import React, {
    useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { motion } from 'framer-motion';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';
import { DealSelectorVariant } from '@r6digital/rapidstor-types/react/entities/Journey';

import {
    usePatchReservationMutation,
} from '@/core/api/rapidstor';
import { useExternalData } from '@/core/hooks/useExternalData';
import Logger from '@/core/utils/logging';
import {
    payLaterButtonText,
    payNowButtonText,
    RapidStorSettings,
    saveQuoteButtonText,
    storedNeededDate,
} from '@/core/utils/rapidstor';
import {
    addUpsellSelection,
    clearUpsellFunnel,
    selectCurrentDeal,
    selectContactData,
    selectUpsell,
    selectUpsellFunnel,
    setReservationData,
    setUpsellFunnel,
} from '@/store/bookingSlice';
import { fadeIn } from '@/core/utils/ui';

import store from '@/store';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import Header from '@/core/components/elements/header';
import Spinner from '@/core/components/elements/spinner';
import JourneyProgress from '@/core/components/journey-progress';
import OptionSelector from '@/journey-based-listing/components/UnitSelect/OptionSelector';

import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
    cardButtonsVariants,
} from '@/features/rapidstor/animations';
import {
    reservationEvent,
    reservationViaMoveInEvent,
} from '@/features/rapidstor/PostMessageEvents';
import { AnimationContextProvider } from '@/features/rapidstor/providers/AnimationProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import ReservationService from '@/features/rapidstor/services/ReservationService';
import {
    buildContinuePayload,
    buildCustomiseBookingPayload,
    buildUpsellSelectedPayload,
    collectOfferedUnitTypeIds,
    CUSTOMISE_BOOKING_CONTINUE,
    CUSTOMISE_BOOKING_UPSELL_SELECTED,
    CUSTOMISE_BOOKING_VIEWED,
    displayedOptionUnitTypes,
    summariseUnitType,
} from '@/journey-based-listing/utils/customiseBookingTracking';
import { resolveDisplayedOptions } from '@/journey-based-listing/utils/displayedOptions';

import type { DealSelectorProps, DescriptorSwitchConfirmProps } from '@/journey-based-listing/components/UnitSelect/OptionSelector';
import DealRibbon from '@/journey-based-listing/components/UnitSelect/deal-selector/DealRibbon';
import DealRibbonConfirm from '@/journey-based-listing/components/UnitSelect/deal-selector/DealRibbonConfirm';
import DealToggleBar from '@/journey-based-listing/components/UnitSelect/deal-selector/DealToggleBar';
import DealToggleBarConfirm from '@/journey-based-listing/components/UnitSelect/deal-selector/DealToggleBarConfirm';
import DealDrawer from '@/journey-based-listing/components/UnitSelect/deal-selector/DealDrawer';
import DealDrawerConfirm from '@/journey-based-listing/components/UnitSelect/deal-selector/DealDrawerConfirm';
import DealRibbonSlim from '@/journey-based-listing/components/UnitSelect/deal-selector/DealRibbonSlim';

import classes from '@/scss/screens/customise-booking.module.scss';
import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';

interface DealSelectorVariantConfig {
    Selector: React.ComponentType<DealSelectorProps>;
    Confirm: React.ComponentType<DescriptorSwitchConfirmProps>;
    replacesBanner?: boolean;
}

const dealSelectorVariants: Record<DealSelectorVariant, DealSelectorVariantConfig> = {
    ribbon: { Selector: DealRibbon, Confirm: DealRibbonConfirm },
    'ribbon-slim': { Selector: DealRibbonSlim, Confirm: DealRibbonConfirm, replacesBanner: true },
    toggle: { Selector: DealToggleBar, Confirm: DealToggleBarConfirm },
    drawer: { Selector: DealDrawer, Confirm: DealDrawerConfirm },
};

function getDealSelectorVariant(): DealSelectorVariant {
    const journey = window.rapidstor_settings?.app_config?.journey as unknown as Record<string, unknown>;
    const variant = journey?.dealSelectorVariant as string | undefined;
    if (variant && variant in dealSelectorVariants) {
        return variant as DealSelectorVariant;
    }
    return 'ribbon';
}

export default function CustomiseBooking() {
    const location = useLoaderData() as Location;
    const upSell = useSelector(selectUpsell);
    const contactData = useSelector(selectContactData);
    const upsellFunnel = useSelector(selectUpsellFunnel);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        unitData,
        reservationData,
        toggleContactCard,
        selectedDescriptor,
        selectedUnitType,
        setSelectedDescriptor,
        setSelectedUnitType,
        persona,
        handleMakeReservation,
        dealSelectorVariant,
    } = useContext(JourneyContext);

    const [cookies] = useCookies(['contactData', 'sessionId']);
    const { generateExternalData } = useExternalData();
    const [patchReservation] = usePatchReservationMutation();
    const { trackEvent } = useRapidStorTracking();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [patchError, setPatchError] = useState<boolean>(false);
    const [selectedID, setSelectedID] = useState<string>('');
    const syncedDescriptorRef = useRef<string>('');
    const trackedViewRef = useRef<boolean>(false);
    const reportedSelectionsRef = useRef<Set<string>>(new Set());

    const currentDeal = useSelector(selectCurrentDeal);
    const effectiveDealId = dealSelectorVariant
        ? currentDeal.dealId ?? null
        : reservationData?.deal || null;
    const variantConfig = dealSelectorVariant
        ? dealSelectorVariants[getDealSelectorVariant()]
        : null;
    const DealSelectorComponent = variantConfig?.Selector;
    const DescriptorSwitchConfirmComponent = variantConfig?.Confirm;
    const dealSelectorReplacesBanner = variantConfig?.replacesBanner ?? false;

    useEffect(() => {
        fadeIn();
    }, []);

    // A failed save is about the selection it was made with. A new selection starts clean.
    useEffect(() => {
        setPatchError(false);
    }, [selectedUnitType, effectiveDealId]);

    useEffect(() => {
        if (!unitData || !reservationData) {
            // Entered without the data to build an anchor. Drop any anchor left by an
            // earlier visit so a later continue event cannot be measured against it.
            store.dispatch(clearUpsellFunnel());
            navigate('/');
        }
    }, [navigate, reservationData, unitData]);

    useEffect(() => {
        if (reservationData?.descriptor) {
            setSelectedDescriptor(reservationData.descriptor);
            // Only reset selectedUnitType and selectedID when the descriptor changes
            // (initial load or descriptor switch). Skipping on deal-only changes preserves
            // the user's alternative unit selection so Pay Now sends the correct unit.
            if (reservationData.descriptor._id !== syncedDescriptorRef.current) {
                setSelectedUnitType(reservationData.unitType);
                setSelectedID(reservationData.unitType._id);
                syncedDescriptorRef.current = reservationData.descriptor._id;
            }
        }
    }, [navigate, location, reservationData, setSelectedDescriptor, setSelectedUnitType]);

    /**
     * What the screen displays, resolved once and shared by the desktop button row and
     * the tracking event. OptionSelector reads the same resolver, so the three agree.
     */
    const displayed = useMemo(() => {
        if (!unitData || !reservationData?.descriptor) {
            return null;
        }
        return resolveDisplayedOptions({
            descriptor: reservationData.descriptor,
            descriptors: unitData.descriptors ?? [],
            location,
            reservedUnitType: reservationData.unitType,
            upSell,
        });
    }, [location, reservationData, unitData, upSell]);

    /**
     * Records what the visitor was shown on arrival: the base selection and any upsell
     * or upgrade beside it. Fires once per entry — OptionSelector mounts twice on this
     * screen, so the event cannot live there, and the ref keeps deal switches and the
     * StrictMode double-mount from sending a second one.
     *
     * There is deliberately no unmount cleanup clearing the anchor. `setUpsellFunnel`
     * replaces it wholesale, so each arrival starts fresh; a cleanup would fire during the
     * StrictMode mount-unmount-remount cycle and wipe the anchor that the ref guard then
     * refuses to write again.
     *
     * NOTE: Wrapped in a try catch with non blocking error handling as this is a
     * non-critical event. If this fails the user flow for reservation should not be impacted.
     */
    useEffect(() => {
        if (trackedViewRef.current || !displayed || !reservationData) {
            return;
        }
        trackedViewRef.current = true;

        try {
            const payload = buildCustomiseBookingPayload({
                displayed,
                descriptor: reservationData.descriptor,
                selectedUnitType: reservationData.unitType,
                dealId: effectiveDealId,
                usingDealSelectorVariant: !!dealSelectorVariant,
            });

            // The anchor is recorded first: taking an upsell replaces the descriptor, so
            // this is the only moment the arrival selection can still be captured.
            store.dispatch(setUpsellFunnel({
                base: payload.base,
                baseDescriptorId: reservationData.descriptor._id,
                upsellType: payload.upsellType,
                offeredUnitTypeIds: collectOfferedUnitTypeIds(
                    displayed,
                    reservationData.descriptor.name,
                ),
            }));

            trackEvent(CUSTOMISE_BOOKING_VIEWED, payload);
        } catch (error) {
            Logger.warn(
                `[customise-booking.tsx] Error thrown in customise booking view track event: ${JSON.stringify(
                    error,
                )}`,
                location,
            );
        }
    }, [dealSelectorVariant, displayed, effectiveDealId, location, reservationData, trackEvent]);

    /**
     * Records a visitor taking one of the options they were offered.
     *
     * Watches the committed selection rather than hooking the click handlers: an option
     * row, a confirmed descriptor switch and the upsell card's Upgrade button all end up
     * changing `selectedUnitType`, so one effect covers all three. It also means an
     * opened-then-cancelled descriptor confirm records nothing, which is intended.
     */
    useEffect(() => {
        const selectedUnitTypeId = selectedUnitType?._id;
        if (
            !upsellFunnel
            || !selectedUnitTypeId
            || selectedUnitTypeId === upsellFunnel.base.unitTypeRs
            || !upsellFunnel.offeredUnitTypeIds.includes(selectedUnitTypeId)
            || reportedSelectionsRef.current.has(selectedUnitTypeId)
        ) {
            return;
        }
        reportedSelectionsRef.current.add(selectedUnitTypeId);

        try {
            store.dispatch(addUpsellSelection(selectedUnitTypeId));

            // Same lookup the viewed event's `upsells` rows use, so a descriptor-kind
            // selection records the descriptor's copy instead of falling back to the floor.
            const selectedRow = displayed
                ? displayedOptionUnitTypes(displayed, reservationData?.descriptor.name ?? '')
                    .find((row) => row.unitType._id === selectedUnitTypeId)
                : undefined;

            trackEvent(
                CUSTOMISE_BOOKING_UPSELL_SELECTED,
                buildUpsellSelectedPayload(
                    upsellFunnel,
                    summariseUnitType({
                        unitType: selectedUnitType,
                        descriptorName: selectedDescriptor?.name
                            ?? reservationData?.descriptor.name ?? '',
                        dealId: effectiveDealId,
                        optionText: selectedDescriptor?.jblOptionText,
                        upgradeDescriptor: selectedRow?.upgradeDescriptor,
                    }),
                    reportedSelectionsRef.current.size,
                ),
            );
        } catch (error) {
            Logger.warn(
                `[customise-booking.tsx] Error thrown in upsell selected track event: ${JSON.stringify(
                    error,
                )}`,
                location,
            );
        }
    }, [
        displayed,
        effectiveDealId,
        location,
        reservationData?.descriptor.name,
        selectedDescriptor?.jblOptionText,
        selectedDescriptor?.name,
        selectedUnitType,
        trackEvent,
        upsellFunnel,
    ]);

    const hasMultipleOptions = displayed?.hasMultipleOptions ?? false;

    if (!unitData || !reservationData) {
        return null;
    }

    /**
     * NOTE: Wrapped in a try catch with non blocking error handling as this is a non-critical event
     * If this fails the user flow for reservation should not be impacted.
     */
    const sendReservationTrackingEvent = () => {
        try {
            trackEvent('reservation', {});
        } catch (error) {
            Logger.warn(
                `[customise-booking.tsx] Error thrown in POST track event request: ${JSON.stringify(
                    error,
                )}`,
                location,
            );
        }
    };

    /**
     * Closes the upsell funnel: the visitor is on their way to move-in, and this says
     * whether the unit they are taking is the one they arrived on.
     *
     * Fires immediately before the navigation rather than off the reservation call, so it
     * marks reaching move-in. The POST branch returns early on failure and never gets
     * here; the PATCH branch navigates either way, and the visitor does reach move-in.
     *
     * NOTE: Wrapped in a try catch with non blocking error handling as this is a
     * non-critical event. If this fails the user flow for reservation should not be impacted.
     */
    const sendContinueTrackingEvent = (forMoveIn: boolean, isNewReservation: boolean) => {
        if (!upsellFunnel || !selectedUnitType || !selectedDescriptor) {
            return;
        }

        try {
            trackEvent(
                CUSTOMISE_BOOKING_CONTINUE,
                buildContinuePayload({
                    funnel: upsellFunnel,
                    reserved: summariseUnitType({
                        unitType: selectedUnitType,
                        descriptorName: selectedDescriptor.name,
                        dealId: effectiveDealId,
                        optionText: selectedDescriptor.jblOptionText,
                    }),
                    reservedDescriptorId: selectedDescriptor._id,
                    viaMoveIn: forMoveIn,
                    isNewReservation,
                }),
            );
        } catch (error) {
            Logger.warn(
                `[customise-booking.tsx] Error thrown in customise booking continue track event: ${JSON.stringify(
                    error,
                )}`,
                location,
            );
        }
    };

    const constructReservationPayload = async (forMoveIn: boolean) => {
        if (!reservationData || !selectedUnitType || !selectedDescriptor) {
            return;
        }

        setIsLoading(true);
        const reservationSvc = new ReservationService(location);
        // Undefined when nothing is stored: the PATCH then keeps the reservation's date, and
        // handleMakeReservation gives the POST the site's default.
        const neededDate = storedNeededDate(contactData, cookies);

        // If there is already a reservation token stored then only patch the existing reservation with new data
        // Otherwise create a new reservation, rapidstor track event and window tracking event
        if (reservationData?.token) {
            setPatchError(false);
            try {
                const patchPayload = reservationSvc.buildPatchPayload(
                    reservationData.token,
                    neededDate,
                    selectedUnitType._id,
                    reservationData.deal ?? null,
                    {},
                    {},
                );
                await patchReservation(patchPayload).unwrap();

                Logger.info(
                    '[customise-booking.tsx] PATCH reservation request',
                    location,
                );
            } catch (err) {
                reservationSvc.handleError(
                    '[customise-booking.tsx] Error thrown in PATCH reservation request',
                    JSON.stringify(err),
                );
                // The wizard would load whatever the API still holds, not what the customer
                // just chose. Stay here and say so instead.
                setPatchError(true);
                setIsLoading(false);
                return;
            }

            sendContinueTrackingEvent(forMoveIn, false);

            // Navigate To Booking Wizard
            navigate(`/move-in/${reservationData.token}`);
        } else {
            try {
                // Post Reservation To RapidStor Backend
                const contactDataToUse = contactData || cookies.contactData;

                const reservationDataFresh = {
                    ...reservationData,
                    descriptor: selectedDescriptor,
                    unitType: selectedUnitType,
                };

                const payload = reservationSvc.buildPostPayload(
                    reservationDataFresh,
                    false,
                    false,
                    contactDataToUse,
                    neededDate,
                    generateExternalData(),
                );

                if (persona) {
                    payload.payload.persona = persona;
                }

                const result = await handleMakeReservation(payload, selectedDescriptor._id);
                if (!result) {
                    setIsLoading(false);
                    return;
                }

                Logger.info(
                    '[customise-booking.tsx] POST reservation request',
                    location,
                    [{ name: 'rapidstor_reservationToken', value: result.token }],
                );

                // Save Reservation Data to store
                if (selectedDescriptor) {
                    Logger.info(
                        '[customise-booking.tsx] Saving fresh descriptor data to reservationData',
                    );
                    store.dispatch(
                        setReservationData({
                            ...reservationData,
                            unitType: selectedUnitType,
                            descriptor: selectedDescriptor,
                            token: result.token,
                        }),
                    );
                }

                // Send Reservation Window Tracking Event
                const eventPayload = {
                    site_info: {
                        location_code: location.locationCode,
                        site_token: location.token,
                    },
                    transaction_id: result.token,
                } as ReservationEventPayloadStandard;

                if (forMoveIn) {
                    reservationViaMoveInEvent(eventPayload);
                } else {
                    reservationEvent(eventPayload);
                }

                // RapidStor reservation track event
                sendReservationTrackingEvent();

                sendContinueTrackingEvent(forMoveIn, true);

                // Navigate To Move in Wizard
                navigate(`/move-in/${result.token}`);
            } catch (err) {
                reservationSvc.handleError(
                    '[customise-booking.tsx] Error thrown in POST reservation request',
                    JSON.stringify(err),
                    [
                        {
                            name: 'rapidstor_reservationDescriptor',
                            value: reservationData.descriptor._id,
                        },
                        {
                            name: 'rapidstor_reservationUnitType',
                            value: reservationData.unitType._id,
                        },
                        {
                            name: 'rapidstor_reservationNeededDate',
                            value: neededDate ?? '',
                        },
                    ],
                );
                setIsLoading(false);
            }
        }
    };

    return (
        <>
            <div style={{
                padding: '0 12px',
            }}
            >
                <JourneyProgress mobile />
                <div>
                    <div className="d-flex">
                        <Header
                            level={1}
                            className={`${classes.title} ${classes.mobile}`}
                        >
                            <span className="">
                                {t('customise_booking:header_mobile')}
                            </span>
                        </Header>
                        <Header
                            level={1}
                            className={`${classes.title} ${classes.desktop}`}
                        >
                            <span>
                                {t('book_unit:header_1')}
                                <span className={classes.underline}>
                                    {t('book_unit:header_2')}
                                </span>
                                {t('book_unit:header_3')}
                            </span>
                        </Header>
                    </div>
                    <JourneyProgress desktop />
                </div>
                {reservationData && reservationData.unitType && selectedUnitType && displayed && (
                    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                        {patchError && (
                            <Alert
                                type="error"
                                text={t('customise_booking:booking_details_error')}
                                fitContent
                            />
                        )}
                        <AnimationContextProvider>
                            <div className={`${classes.mobile}`}>
                                <OptionSelector
                                    descriptor={reservationData.descriptor}
                                    unitType={reservationData.unitType}
                                    location={location}
                                    upSell={upSell}
                                    displayed={displayed}
                                    deal={dealSelectorVariant ? (currentDeal.dealId ?? null) : (reservationData.deal ?? null)}
                                    makeReservation={() => constructReservationPayload(false)}
                                    loading={isLoading}
                                    setLoading={setIsLoading}
                                    reservationToken={reservationData?.token}
                                    selectedID={selectedID}
                                    setSelectedID={setSelectedID}
                                    usingDealSelectorVariant={!!dealSelectorVariant}
                                    dealSelectorReplacesBanner={dealSelectorReplacesBanner}
                                    DealSelectorComponent={dealSelectorVariant ? DealSelectorComponent : undefined}
                                    DescriptorSwitchConfirmComponent={dealSelectorVariant ? DescriptorSwitchConfirmComponent : undefined}
                                />
                            </div>
                        </AnimationContextProvider>
                        <AnimationContextProvider>
                            <div className={`${classes.desktop}`}>
                                <OptionSelector
                                    descriptor={reservationData.descriptor}
                                    unitType={reservationData.unitType}
                                    location={location}
                                    upSell={upSell}
                                    displayed={displayed}
                                    deal={dealSelectorVariant ? (currentDeal.dealId ?? null) : (reservationData.deal ?? null)}
                                    makeReservation={() => constructReservationPayload(false)}
                                    desktop
                                    loading={isLoading}
                                    setLoading={setIsLoading}
                                    reservationToken={reservationData?.token}
                                    selectedID={selectedID}
                                    setSelectedID={setSelectedID}
                                    usingDealSelectorVariant={!!dealSelectorVariant}
                                    dealSelectorReplacesBanner={dealSelectorReplacesBanner}
                                    DealSelectorComponent={dealSelectorVariant ? DealSelectorComponent : undefined}
                                    DescriptorSwitchConfirmComponent={dealSelectorVariant ? DescriptorSwitchConfirmComponent : undefined}
                                />
                            </div>
                        </AnimationContextProvider>
                    </div>
                )}
            </div>

            {hasMultipleOptions && (
                <motion.div
                    layout
                    variants={cardButtonsVariants}
                    initial="start"
                    animate="show"
                    transition={{
                        duration: ANIMATION_SPEED,
                        delay: ANIMATION_DELAY * 2,
                    }}
                >
                    <div className={`${classes.buttons} ${classes.desktop}`}>
                        {RapidStorSettings(location).showSaveQuoteButton() && (
                            <Button
                                onclick={() => {
                                    const effectiveDeal = dealSelectorVariant ? (currentDeal.dealId || undefined) : (reservationData.deal || undefined);
                                    toggleContactCard('quote', selectedUnitType, effectiveDeal);
                                }}
                                disabled={isLoading}
                                type="outlineOnBackground"
                                size="medium"
                                testId="save-quote"
                            >
                                {saveQuoteButtonText(location)}
                            </Button>
                        )}
                        {RapidStorSettings(location).showReservationButton() && (
                            <Button
                                onclick={() => {
                                    const effectiveDeal = dealSelectorVariant ? (currentDeal.dealId || undefined) : (reservationData.deal || undefined);
                                    toggleContactCard('reservation', selectedUnitType, effectiveDeal);
                                }}
                                disabled={isLoading}
                                type="outlineOnBackground"
                                size="medium"
                                testId="quick-reserve"
                            >
                                {payLaterButtonText(location)}
                            </Button>
                        )}
                        {RapidStorSettings(location).showMoveInButton() && (
                            <Button
                                onclick={() => {
                                    constructReservationPayload(true);
                                }}
                                disabled={isLoading}
                                type="primary"
                                size="medium"
                                testId="pay-now"
                            >
                                {isLoading ? (
                                    <Spinner inverted />
                                ) : (
                                    payNowButtonText(location)
                                )}
                            </Button>
                        )}
                    </div>
                </motion.div>
            )}
        </>
    );
}
